import { getEffectiveTranslations } from './i18n';
function t(lang,key){return getEffectiveTranslations(lang||'en')[key]||key;}

// ── Engagement & retention helpers ───────────────────────────────────────
// Streak computed from user_daily_usage — consecutive days with at least one action

// Get streak from DB
export async function getStreak(userId) {
  if (!userId) return { days: 0, lastActive: null, boosted: false };
  try {
    const { db } = await import('./supabase');
    // Get last 30 days of activity
    const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
    const { data } = await db.user_daily_usage()
      ?.select('date, likes_count, super_likes_count, passes_count')
      .eq('user_id', userId)
      .gte('date', cutoff)
      .order('date', { ascending: false }) || {};

    if (!data?.length) return { days: 0, lastActive: null, boosted: false };

    // Count consecutive active days (at least 1 action)
    const activeDates = new Set(
      data.filter(r => r.likes_count + r.super_likes_count + r.passes_count > 0)
          .map(r => r.date)
    );

    let streak = 0;
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      if (activeDates.has(dateStr)) {
        streak++;
      } else {
        // Allow 1-day gap for today (hasn't acted yet today)
        if (i === 0) continue;
        break;
      }
    }

    // Check active boost
    const { db: db2 } = await import('./supabase');
    const { data: boost } = await db2.user_boosts()
      ?.select('expires_at')
      .eq('user_id', userId)
      .gt('expires_at', new Date().toISOString())
      .limit(1)
      .single() || {};

    return {
      days:       streak,
      lastActive: data[0]?.date || null,
      boosted:    !!boost,
      boostEndsIn: boost ? new Date(boost.expires_at).getTime() - Date.now() : null,
    };
  } catch {
    return { days: 0, lastActive: null, boosted: false };
  }
}

// ── Activate boost — write to DB ─────────────────────────────────────────
export async function activateBoost(userId) {
  if (!userId) return { success: false };
  try {
    const { supabase } = await import('./supabase');
    const { error } = await supabase
      ?.from('user_boosts')
      .insert({
        user_id:    userId,
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        multiplier: 3.0,
      });
    return { success: !error, endsAt: Date.now() + 30 * 60 * 1000 };
  } catch {
    return { success: false };
  }
}

// ── Nudge messages for empty states ──────────────────────────────────────
export function getEmptyNudges(lang) { return [
  { headline: t(lang,'engage.no_more_title'), sub: t(lang,'engage.no_more_sub') },
  { headline: t(lang,'engage.patient_title'), sub: t(lang,'engage.patient_sub') },
  { headline: t(lang,'engage.seen_all_title'), sub: t(lang,'engage.seen_all_sub') },
  { headline: t(lang,'engage.score_title'), sub: t(lang,'engage.score_sub') },
]; }
export const EMPTY_NUDGES = getEmptyNudges('en');

// ── Icebreakers based on compatibility quiz answers (10-axis model) ──────
// Axes: repair, conflict, accountability, generosity, presence,
//       narrative, stability, boredom, social, stakes
export function getIcebreakers(myAnswers, theirAnswers, theirName) {
  if (!myAnswers || !theirAnswers) return getGenericIcebreakers(theirName);
  const firstName = theirName?.split(' ')[0] || 'them';
  const suggestions = [];
  const sharedKeys = Object.keys(myAnswers).filter(k => myAnswers[k] === theirAnswers[k]);

  // Shared presence → depth question
  if (sharedKeys.includes('presence')) {
    const l = { 1:'easily distracted', 2:'often multitasking', 3:'mostly present', 4:'fully there' };
    suggestions.push(`You're both ${l[myAnswers.presence] || 'similar when it comes to presence'} in conversations — has that ever made a real difference in a relationship?`);
  }
  // Shared social energy → last Friday question
  if (sharedKeys.includes('social')) {
    const l = { 1:'both recharge solo', 2:'both prefer small groups', 3:'both like a mix', 4:'both love a big crowd' };
    suggestions.push(`You ${l[myAnswers.social] || 'share the same social vibe'} — what's the last thing you did on a Friday that felt just right?`);
  }
  // Shared adventure level → novelty question
  if (sharedKeys.includes('boredom')) {
    const l = { 1:'love comfort and routine', 2:'like occasional novelty', 3:'crave variety', 4:'live for new experiences' };
    suggestions.push(`You both ${l[myAnswers.boredom] || 'have similar energy for adventure'} — what's the last spontaneous thing you did?`);
  }
  // Different conflict style → productive tension question
  if (myAnswers.conflict != null && theirAnswers.conflict != null && myAnswers.conflict !== theirAnswers.conflict) {
    suggestions.push(`You handle tension differently — do you think that creates balance, or has it ever been a dealbreaker for you?`);
  }
  // Different stakes/ambition → compatibility question
  if (myAnswers.stakes != null && theirAnswers.stakes != null && Math.abs(myAnswers.stakes - theirAnswers.stakes) >= 2) {
    const l = { 1:'laid-back', 2:'balanced', 3:'driven', 4:'highly ambitious' };
    suggestions.push(`You're ${l[myAnswers.stakes] || 'different'} and ${firstName} is ${l[theirAnswers.stakes] || 'different'} about ambition — how do you think those two worlds fit together?`);
  }
  // Shared repair style → trust question
  if (sharedKeys.includes('repair')) {
    suggestions.push(`You both tend to address things head-on after conflict — what made you that way?`);
  }

  suggestions.push(...getGenericIcebreakers(firstName).slice(0, 2));
  return suggestions.slice(0, 4);
}

function getGenericIcebreakers(firstName) {
  const name = firstName?.split(' ')[0] || 'you';
  return [
    `What's something about ${name} that your profile doesn't show?`,
    `If we were meeting for the first time, where would you pick and why?`,
    `What's the last thing that genuinely made you laugh out loud?`,
    `What's something you're looking forward to in the next 3 months?`,
    `What did you do last weekend that you would actually recommend?`,
  ];
}
